import React from "react";

import { bindActionCreators } from "@reduxjs/toolkit";
import { connect } from "react-redux";

import Drawer from "@busbud/design-system-components/dist/Drawer";
import { useTheme } from "@busbud/design-system-components/dist/styles/useTheme";

import { useOpenDrawerOnNewDrawer } from "@app/components/drawers/hooks";
import { closeDrawer } from "@app/modules/checkout/actions";
import type { DrawerType } from "@app/types/drawer";

import type { RootState } from "../../store/reducers";
import type { Dispatch } from "@reduxjs/toolkit";

import { CartItinerary } from "./cart-itinerary";
import { PublicTripItinerary } from "./public-trip-itinerary";
import { ResultsFilters } from "./results-filters/results-filters";
import { SeatSelection } from "./seat-selection";
import { SelectionOptions } from "./selection-options";
import { SummaryDrawer } from "./summary-drawer";

interface OwnProps {}

interface ReduxProps {
  drawer: DrawerType | null;
  closeDrawer: () => void;
}

const getDrawerContent = (drawer: DrawerType | null, onClose: () => void) => {
  if (!drawer) {
    return null;
  }

  switch (drawer.type) {
    case "cart-itinerary":
      return (
        <CartItinerary
          direction={drawer.direction}
          departure_id={drawer.departure_id}
          onClose={onClose}
        />
      );
    case "public-trip-itinerary":
      return (
        <PublicTripItinerary
          direction={drawer.direction}
          trip={drawer.trip}
          onClose={onClose}
        />
      );
    case "selection-options":
      return (
        <SelectionOptions
          leg_card_id={drawer.leg_card_id}
          journey_id={drawer.journey_id}
          onClose={onClose}
        />
      );
    case "seat-selection":
      return (
        <SeatSelection
          outbound_leg_card_id_class_and_fare={
            drawer.outbound_leg_card_id_class_and_fare
          }
          return_leg_card_id_class_and_fare={
            drawer.return_leg_card_id_class_and_fare
          }
          onClose={onClose}
          segment_id={drawer.segment_id}
        />
      );
    case "results-filters":
      return <ResultsFilters />;
    case "summary-card":
      return (
        <SummaryDrawer
          departure_id={drawer.departure_id}
          terms_props={drawer.terms_props}
          fare={drawer.fare}
          direction={drawer.direction}
          is_roundtrip={drawer.is_roundtrip}
          amenities={drawer.amenities}
          onClose={onClose}
        />
      );
    default:
      return null;
  }
};

const DrawerManager: React.FC<ReduxProps & OwnProps> = ({
  closeDrawer: dispatchCloseDrawerAction,
  drawer
}) => {
  const theme = useTheme();

  const onClose = () => {
    // @ts-expect-error
    drawer?.onClose?.();
    // close action is dispatch twice (here and when calling closeDrawer action)
    dispatchCloseDrawerAction();
  };

  const { isDrawerOpen, closeDrawer } = useOpenDrawerOnNewDrawer(
    drawer,
    onClose
  );

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={closeDrawer}
      BoxProps={{
        width: theme.busbud.spacing(48),
        height: `calc(100% - ${theme.busbud.spacing(2)})`, // This fixes drawer for Chrome 80x
        overflow: "scroll" // This fixes drawer for Chrome 80x
      }}
      SlideProps={{
        onExited: () => onClose()
      }}
      ModalProps={{
        // @ts-expect-error
        ["data-cy"]: drawer?.type
      }}
    >
      {getDrawerContent(drawer, closeDrawer)}
    </Drawer>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators({ closeDrawer }, dispatch);

const mapStateToProps = (state: RootState) => {
  const drawer = state.drawers[state.drawers.length - 1] || null;

  return { drawer };
};

const DrawerManagerComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(DrawerManager);

export { DrawerManagerComponent as DrawerManager };
